import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  ThemeProvider, createTheme, CssBaseline, Box, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Typography, Divider, Avatar, useTheme,
} from '@mui/material';
import { Toaster } from 'react-hot-toast';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useStore } from './store/useStore';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import InspectionsPage from './pages/InspectionsPage';
import ReportPage from './pages/ReportPage';
import ScanPage from './pages/ScanPage';
import TenantPage from './pages/TenantPage';
import PropertyScanFormPage from './pages/PropertyScanFormPage';
import PropertyScanReportPage from './pages/PropertyScanReportPage';
import ProductCatalogPage from './pages/ProductCatalogPage';
import SettingsPage from './pages/SettingsPage';
import { api } from './services/api';

const palette = {
  mode: 'dark' as const,
  primary: { main: '#06B6D4' },
  background: { default: '#07090B', paper: '#0E1114' },
  text: { primary: '#E6EEF3', secondary: '#94A3B8' },
  common: { black: '#000', white: '#fff' },
};

const softBorder = `1px solid ${alpha(palette.text.primary, 0.04)}`;

const theme = createTheme({
  palette: { mode: 'dark', primary: { main: '#00AAFF' }, background: { default: '#0A0A0C', paper: '#121417' } },
  typography: { fontFamily: '"Inter", "Roboto", sans-serif' },
  components: {
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 16,
          backgroundColor: theme.palette.background.paper,
          border: softBorder,
          boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.25)}`,
        })
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          textTransform: 'none',
          fontWeight: 700
        },
        containedPrimary: {
          boxShadow: 'none'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          backgroundColor: 'background.paper',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: softBorder },
        })
      }
    }
  }
});

const DRAWER_WIDTH = 220;

const BASE_NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Products', icon: <PaletteIcon />, path: '/products' },
  { label: 'Projects', icon: <FolderIcon />, path: '/projects' },
  { label: 'Inspections', icon: <AssignmentIcon />, path: '/inspections' },
];

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useStore();
  const NAV_ITEMS = user?.role === 'admin'
    ? [...BASE_NAV_ITEMS, { label: 'Team', icon: <BusinessIcon />, path: '/tenant' }, { label: 'Settings', icon: <SettingsIcon />, path: '/settings' }]
    : BASE_NAV_ITEMS;

  const handleLogout = () => { api.logout(); clearAuth(); navigate('/login'); };

  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#0A0A0C' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: '#0A0A0C', borderRight: '1px solid rgba(255,255,255,0.08)', boxSizing: 'border-box' },
        }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography sx={{ color: '#00AAFF', fontWeight: 'bold', fontSize: 20 }}>Surface IQ</Typography>
          <Typography sx={{ color: '#6E7681', fontSize: 11 }}>Construction</Typography>
        </Box>
        <List sx={{ pt: 1 }}>
          {NAV_ITEMS.map(item => (
            <ListItemButton
              key={item.path}
              selected={location.pathname.startsWith(item.path)}
              onClick={() => navigate(item.path)}
              sx={{
                mx: 1, borderRadius: 1, mb: 0.5,
                '&.Mui-selected': { bgcolor: 'rgba(0,170,255,0.12)', '& .MuiListItemIcon-root': { color: '#00AAFF' }, '& .MuiListItemText-primary': { color: '#00AAFF' } },
                '& .MuiListItemIcon-root': { color: '#6E7681', minWidth: 36 },
                '& .MuiListItemText-primary': { color: '#8A8F98', fontSize: 14 },
              }}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: '#00AAFF', fontSize: 13, mr: 1 }}>
              {user?.full_name?.[0] ?? 'U'}
            </Avatar>
            <Box>
              <Typography sx={{ color: '#F5F7FA', fontSize: 13, fontWeight: 600 }}>{user?.full_name}</Typography>
              <Typography sx={{ color: '#6E7681', fontSize: 11 }}>{user?.role}</Typography>
            </Box>
          </Box>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, px: 1, '& .MuiListItemIcon-root': { color: 'error.main', minWidth: 32 } }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error.main', fontSize: 13 }} />
            </ListItemButton>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flex: 1, overflow: 'auto' }}>
        <Outlet />
      </Box>
    </Box>
  );
}

function PrivateRoute() {
  const token = useStore(s => s.token);
  return token ? <Outlet /> : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster position="top-right" toastOptions={{ style: { background: theme.palette.background.paper, color: theme.palette.text.primary, border: softBorder } }} />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route element={<PrivateRoute />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/products" element={<ProductCatalogPage />} />
              <Route path="/property-scans/new" element={<PropertyScanFormPage />} />
              <Route path="/property-scans/:scanId" element={<PropertyScanReportPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/inspections" element={<InspectionsPage />} />
              <Route path="/scan" element={<ScanPage />} />
              <Route path="/tenant" element={<TenantPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/report/:inspectionId" element={<ReportPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
